import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Card, CardContent, TextField, Button, Grid, Alert, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, CircularProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar } from "@mui/material";
import { CloudUpload as CloudUploadIcon, CheckCircle as CheckCircleIcon, Pending as PendingIcon, Cancel as CancelIcon, Visibility as VisibilityIcon, Info as InfoIcon, Add as AddIcon, Delete as DeleteIcon, } from "@mui/icons-material";
import { getData, postData, putData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

export const getAbsoluteFileUrl = (fileData) => {
    if (!fileData) return "";
    const data = Array.isArray(fileData) ? fileData[0] : fileData;
    if (!data) return "";

    if (typeof data === "string") {
        return data.startsWith("http")
            ? data
            : `${API_ENDPOINTS.defaults.baseURL.replace(/\/api$/, "").replace(/\/$/, "")}${data.startsWith("/") ? data : `/${data}`}`;
    }

    let path = data.formUrl || data.form_url || data.url || data.filePath || data.file_url || data.proofUrl || data.form12A || data.file || data.path;

    if (!path) return "";
    if (path.startsWith("http")) return path;

    let baseUrl = API_ENDPOINTS.defaults.baseURL || "";
    let origin = baseUrl.replace(/\/api$/, "").replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    return `${origin}${normalizedPath}`;
};

const SECTION_LIMITS = {
    "80C": 150000,
    "80D": 50000,
    "HRA": 1000000,
    "homeLoanInterest": 200000,
    "NPS": 50000,
    "80E": Infinity,
    "80G": Infinity,
};

const EmployeeITDeclarations = () => {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const [financialYear, setFinancialYear] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [taxSummary, setTaxSummary] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");
    const taxSummaryRef = useRef(null);
    const declarationsRef = useRef(null);

    useEffect(() => {
        taxSummaryRef.current = taxSummary;
    }, [taxSummary]);

    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => {
                setMessage({ type: "", text: "" });
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [message]);
    const [pendingProofs, setPendingProofs] = useState([]);
    const [taxOptions, setTaxOptions] = useState({});
    const [isFinancialYearEnded, setIsFinancialYearEnded] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [uploadingId, setUploadingId] = useState("");
    const [proofsLoading, setProofsLoading] = useState(false);

    const getSubmissionStatus = useCallback(() => {
        if (!taxSummary?.submissionDeadline) return { isOpen: true, isWarningMonth: false, deadlineFormatted: "N/A" };
        const today = new Date();
        const selectedStartYear = parseInt(financialYear);
        const deadlineParts = taxSummary.submissionDeadline.split("-").map(Number);
        let fyEndDate;
        let displayYear;

        if (deadlineParts.length === 3) {
            // Handles both YYYY-MM-DD and MM-DD-YYYY
            let year, month, day;
            if (deadlineParts[0] > 1000) {
                [year, month, day] = deadlineParts;
            } else {
                [month, day, year] = deadlineParts;
            }
            fyEndDate = new Date(year, month - 1, day, 23, 59, 59);
            displayYear = year;
        } else {
            // MM-DD
            const [month, day] = deadlineParts;
            displayYear = month <= 4 ? selectedStartYear + 1 : selectedStartYear;
            fyEndDate = new Date(displayYear, month - 1, day, 23, 59, 59);
        }

        const isOpen = today <= fyEndDate;
        const diffTime = fyEndDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const isWarningPeriod = isOpen && diffDays <= 30;

        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        let finalMonthIdx, finalDayVal;

        if (deadlineParts.length === 3) {
            if (deadlineParts[0] > 1000) {
                finalMonthIdx = deadlineParts[1] - 1;
                finalDayVal = deadlineParts[2];
            } else {
                finalMonthIdx = deadlineParts[0] - 1;
                finalDayVal = deadlineParts[1];
            }
        } else {
            finalMonthIdx = deadlineParts[0] - 1;
            finalDayVal = deadlineParts[1];
        }

        const deadlineFormatted = `${monthNames[finalMonthIdx]} ${finalDayVal}${finalDayVal === 1 ? 'st' : finalDayVal === 2 ? 'nd' : finalDayVal === 3 ? 'rd' : 'th'}, ${displayYear}`;

        return { isOpen, isWarningMonth: isWarningPeriod, deadlineFormatted };
    }, [financialYear, taxSummary?.submissionDeadline]);

    const { isOpen: isSubmissionOpen, isWarningMonth: isMarchWarning, deadlineFormatted } = getSubmissionStatus();
    const isProofPhase = isSubmissionOpen;
    const [regimeLoading, setRegimeLoading] = useState(false);
    const [validationErrors] = useState({});
    const [form12BData, setForm12BData] = useState(null);
    const [isForm12BLoading, setIsForm12BLoading] = useState(false);

    const showFeedback = useCallback((type, text) => {
        setMessage({ type, text });
        setSnackbarSeverity(type || "info");
        setSnackbarOpen(Boolean(text));
    }, []);

    const handleSnackbarClose = (_, reason) => {
        if (reason === "clickaway") return;
        setSnackbarOpen(false);
    };

    const validateUploadFile = useCallback((file, label = "file") => {
        if (!file) {
            showFeedback("error", `Please select a ${label} to upload.`);
            return false;
        }

        if (file.type !== "application/pdf") {
            showFeedback("error", "Only PDF files are allowed.");
            return false;
        }

        if (file.size > 20 * 1024 * 1024) {
            showFeedback("error", "File size should not exceed 20MB.");
            return false;
        }

        return true;
    }, [showFeedback]);

    const getApiErrorMessage = useCallback((response, fallbackMessage) => {
        if (response?.statusCode === 401) {
            return "Your session has expired or the token is invalid. Please log in again and retry the upload.";
        }

        return (
            response?.message ||
            response?.error?.message ||
            response?.error ||
            fallbackMessage
        );
    }, []);

    const checkFinancialYearStatus = useCallback((startYear) => {
        if (!startYear) return false;

        let fyEndDate;
        if (taxSummary?.submissionDeadline) {
            const parts = taxSummary.submissionDeadline.split("-").map(Number);
            if (parts.length === 3) {
                let year, month, day;
                if (parts[0] > 1000) {
                    [year, month, day] = parts;
                } else {
                    [month, day, year] = parts;
                }
                fyEndDate = new Date(year, month - 1, day, 23, 59, 59, 999);
            } else {
                const [month, day] = parts;
                const endYear = month <= 4 ? parseInt(startYear) + 1 : parseInt(startYear);
                fyEndDate = new Date(endYear, month - 1, day, 23, 59, 59, 999);
            }
        } else {
            fyEndDate = new Date(parseInt(startYear) + 1, 2, 31, 23, 59, 59, 999);
        }

        const currentDate = new Date();
        return currentDate > fyEndDate;
    }, [taxSummary?.submissionDeadline]);

    const fetchForm12BData = useCallback(async () => {
        if (!financialYear) return;

        setIsForm12BLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.getIncomeTaxForm(userId, financialYear));
            if (response && (response.status === "SUCCESS" || response.success)) {
                setForm12BData(response.result);
            } else {
                setForm12BData(null);
            }
        } catch (error) {
            return null;
        } finally {
            setIsForm12BLoading(false);
        }
    }, [financialYear, userId]);

    const handleCalculateTax = useCallback(async (manualGross, manualDeclarations) => {
        const grossAnnual = manualGross !== undefined ? manualGross : taxSummaryRef.current?.grossAnnual;
        const sourceDeclarations = manualDeclarations || declarationsRef.current;

        if (!grossAnnual) {
            if (manualGross === undefined) {
                setMessage({ type: "error", text: "Please enter Annual Gross Salary" });
            }
            return;
        }

        setLoading(true);
        try {
            const currentDeclarations = [];

            if (sourceDeclarations) {
                Object.keys(sourceDeclarations).forEach(key => {
                    sourceDeclarations[key].forEach(item => {
                        if (parseFloat(item.amount) > 0) {
                            currentDeclarations.push({
                                section: key === "hra" ? "HRA" : key === "homeLoanInterest" ? "Home Loan Interest" : key === "nps" ? "NPS" : key === "otherIncome" ? "Other" : key.replace("section", ""),
                                amount: item.amount,
                                investmentType: item.investmentType
                            });
                        }
                    });
                });
            }

            const payload = {
                grossAnnual,
                financialYear,
                declarations: currentDeclarations
            };

            const response = await postData(API_ENDPOINTS.calculateIncomeTax, payload);
            if (response.statusCode === 200) {
                setTaxComparisonData(response.result);
                setTaxSummary(prev => ({
                    ...prev,
                    grossAnnual,
                    oldRegimeTax: response.result.oldRegime.taxPayable,
                    newRegimeTax: response.result.newRegime.taxPayable
                }));
            } else if (manualGross === undefined) {
                setMessage({ type: "error", text: response.message || "Failed to calculate tax" });
            }
        } catch (error) {
            if (manualGross === undefined) {
                setMessage({ type: "error", text: "Error calculating tax" });
            }
        } finally {
            setLoading(false);
        }
    }, [financialYear]);

    const fetchTaxData = useCallback(async () => {
        setLoading(true);
        setProofsLoading(true);

        try {
            const summaryResponse = await getData(API_ENDPOINTS.getTaxSummaryByFinancialYear(userId, financialYear));
            if (summaryResponse.statusCode === 200) {
                setTaxSummary(summaryResponse.result);
            }

            const declarationsResponse = await getData(API_ENDPOINTS.getEmployeeTaxDeclaration(userId, financialYear));
            if (declarationsResponse.statusCode === 200) {
                setPendingProofs(declarationsResponse.result?.filter(p => p.proofUrl) || []);
            }
            if (declarationsResponse.statusCode === 200 && declarationsResponse.result?.length > 0) {
                const reverseMapping = {
                    "80C": "section80C",
                    "80D": "section80D",
                    "HRA": "hra",
                    "homeLoanInterest": "homeLoanInterest",
                    "NPS": "nps",
                    "otherDeductions": "otherIncome"
                };

                const newDeclarations = {
                    section80C: [],
                    section80D: [],
                    hra: [],
                    homeLoanInterest: [],
                    nps: [],
                    otherIncome: []
                };

                declarationsResponse.result.forEach(item => {
                    const stateKey = reverseMapping[item.section];
                    if (stateKey) {
                        newDeclarations[stateKey].push({
                            id: item.id,
                            amount: item.declaredAmount || item.amount,
                            investmentType: item.investmentType,
                            proofUrl: item.proofUrl,
                            proofStatus: item.proofStatus
                        });
                    }
                });

                Object.keys(newDeclarations).forEach(key => {
                    if (newDeclarations[key].length === 0) {
                        const defaultTypeMap = {
                            section80C: "Public Provident Fund (PPF)",
                            section80D: "Medical Insurance Premium",
                            hra: "House Rent Allowance",
                            homeLoanInterest: "Interest on Housing Loan (Self-Occupied)",
                            nps: "NPS Tier-I Contribution (80CCD(1B))",
                            otherIncome: "Interest from Savings Bank A/c (80TTA)"
                        };
                        newDeclarations[key].push({ id: null, amount: "", investmentType: defaultTypeMap[key], proofUrl: null });
                    }
                });

                setDeclarations(newDeclarations);
                setIsSubmitted(true);
            } else {
                setIsSubmitted(false);
            }

            if (summaryResponse.result?.grossAnnual > 0) {
                handleCalculateTax(summaryResponse.result.grossAnnual, {});
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
            setProofsLoading(false);
        }
        fetchForm12BData();
    }, [financialYear, fetchForm12BData, handleCalculateTax, userId]);

    const fetchTaxOptions = useCallback(async () => {
        try {
            const response = await getData(API_ENDPOINTS.getTaxOptions);
            if (response.statusCode === 200) {
                const optionsMap = {};
                response.result.sections.forEach(section => {
                    optionsMap[section.key] = section.types;
                });
                setTaxOptions(optionsMap);

                setDeclarations(prev => ({
                    section80C: [{ amount: "", investmentType: optionsMap["80C"]?.[0] || "PPF" }],
                    section80D: [{ amount: "", investmentType: optionsMap["80D"]?.[0] || "Health Insurance" }],
                    hra: [{ amount: "", investmentType: optionsMap["HRA"]?.[0] || "HRA" }],
                    homeLoanInterest: [{ amount: "", investmentType: optionsMap["homeLoanInterest"]?.[0] || "Home Loan Interest" }],
                    nps: [{ amount: "", investmentType: optionsMap["NPS"]?.[0] || "NPS" }],
                    otherIncome: [{ amount: "", investmentType: optionsMap["otherDeductions"]?.[0] || "Other" }],
                }));
            }
        } catch (error) {
            return null;
        }
    }, []);

    const [declarations, setDeclarations] = useState({
        section80C: [{ id: null, amount: "", investmentType: "PPF", proofUrl: null }],
        section80D: [{ id: null, amount: "", investmentType: "Health Insurance", proofUrl: null }],
        hra: [{ id: null, amount: "", investmentType: "HRA", proofUrl: null }],
        homeLoanInterest: [{ id: null, amount: "", investmentType: "Home Loan Interest", proofUrl: null }],
        nps: [{ id: null, amount: "", investmentType: "NPS", proofUrl: null }],
        otherIncome: [{ id: null, amount: "", investmentType: "Other", proofUrl: null }],
    });

    useEffect(() => {
        declarationsRef.current = declarations;
    }, [declarations]);

    // --- 4. Lifecycle Hooks ---
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const defaultFY = currentMonth >= 3 ? currentYear.toString() : (currentYear - 1).toString();
        setFinancialYear(defaultFY);
        fetchTaxOptions();
    }, [fetchTaxOptions]);

    useEffect(() => {
        if (financialYear) {
            fetchTaxData();
            setIsFinancialYearEnded(checkFinancialYearStatus(financialYear));
        }
    }, [financialYear, fetchTaxData, checkFinancialYearStatus]);

    // --- 5. Event Handlers ---
    const handleRegimeChange = async () => {
        if (!taxSummary || !financialYear) return;
        const newRegime = taxSummary.regime === "Old Regime" ? "New Regime" : "Old Regime";
        const confirmMsg = `Are you sure you want to switch to ${newRegime}? \n\n${newRegime === "New Regime"
            ? "Note: In New Regime, most exemptions (HRA, 80C, etc.) are not available."
            : "Note: In Old Regime, you can claim exemptions like HRA, 80C, etc."
            }`;

        if (window.confirm(confirmMsg)) {
            setRegimeLoading(true);
            const userId = localStorage.getItem("userId");
            try {
                const response = await putData(API_ENDPOINTS.updatePayrollSettings({
                    userId: userId,
                    taxRegime: newRegime
                }));

                if (response.statusCode === 200 || response.success) {
                    setMessage({ type: "success", text: `Successfully switched to ${newRegime}` });
                    fetchTaxData();
                } else {
                    setMessage({ type: "error", text: response.message || "Failed to switch regime" });
                }
            } catch (error) {
                setMessage({ type: "error", text: "An error occurred while switching regime" });
            } finally {
                setRegimeLoading(false);
            }
        }
    };

    const handleDeclarationChange = (field, index, property, value) => {
        setDeclarations((prev) => {
            const newSection = [...prev[field]];
            newSection[index] = { ...newSection[index], [property]: value };
            const nextDeclarations = { ...prev, [field]: newSection };

            if (property === "amount") {
                if (window.taxCalculationTimeout) {
                    clearTimeout(window.taxCalculationTimeout);
                }
                window.taxCalculationTimeout = setTimeout(() => {
                    handleCalculateTax(undefined, nextDeclarations);
                }, 1000);
            }

            return nextDeclarations;
        });
    };

    const handleAddDeclaration = (section) => {
        const defaultTypeMap = {
            section80C: "PPF",
            section80D: "Health Insurance",
            hra: "HRA",
            homeLoanInterest: "Home Loan Interest",
            nps: "NPS",
            otherIncome: "Other"
        };

        setDeclarations(prev => ({
            ...prev,
            [section]: [...prev[section], { id: null, amount: "", investmentType: defaultTypeMap[section], proofUrl: null }]
        }));
    };

    const handleRemoveDeclaration = (section, index) => {
        if (declarations[section].length <= 1) return;
        setDeclarations(prev => ({
            ...prev,
            [section]: prev[section].filter((_, i) => i !== index)
        }));
    };

    const handleSubmitDeclarations = async () => {
        setLoading(true);
        setMessage({ type: "", text: "" });
        const userId = localStorage.getItem("userId");
        const declarationsArray = [];

        const sectionMapping = {
            section80C: "80C",
            section80D: "80D",
            hra: "HRA",
            homeLoanInterest: "homeLoanInterest",
            nps: "NPS",
            otherIncome: "otherDeductions"
        };

        if (Object.keys(validationErrors).length > 0) {
            const firstError = Object.values(validationErrors)[0];
            setMessage({ type: "error", text: firstError });
            setLoading(false);
            return;
        }

        // Soft-capping: Removed frontend blocking for amounts exceeding limits.
        Object.keys(declarations).forEach(key => {
            declarations[key].forEach(item => {
                // Handle commas in amount string before parsing
                const cleanAmount = item.amount ? item.amount.toString().replace(/,/g, "") : "0";
                const amount = parseFloat(cleanAmount);

                if (amount && amount > 0) {
                    declarationsArray.push({
                        section: sectionMapping[key],
                        investmentType: item.investmentType,
                        amount: amount
                    });
                }
            });
        });

        if (declarationsArray.length === 0) {
            setMessage({ type: "error", text: "Please enter at least one declaration amount" });
            setLoading(false);
            return;
        }

        const payload = {
            employeePersonalId: parseInt(userId),
            financialYear: financialYear,
            declarations: declarationsArray,
        };

        try {
            const response = await postData(API_ENDPOINTS.declareEmployeeInvestments, payload);
            if (response.statusCode === 200 || response.statusCode === 201) {
                setMessage({ type: "success", text: "Declarations submitted successfully!" });
                setIsSubmitted(true);
                fetchTaxData();
                setDeclarations({
                    section80C: [{ id: null, amount: "", investmentType: "PPF", proofUrl: null }],
                    section80D: [{ id: null, amount: "", investmentType: "Health Insurance", proofUrl: null }],
                    hra: [{ id: null, amount: "", investmentType: "HRA", proofUrl: null }],
                    homeLoanInterest: [{ id: null, amount: "", investmentType: "Home Loan Interest", proofUrl: null }],
                    nps: [{ id: null, amount: "", investmentType: "NPS", proofUrl: null }],
                    otherIncome: [{ id: null, amount: "", investmentType: "Other", proofUrl: null }],
                });
            } else {
                setMessage({ type: "error", text: response.message || "Failed to submit declarations" });
            }
        } catch (error) {
            setMessage({ type: "error", text: "Error submitting declarations" });
        } finally {
            setLoading(false);
        }
    };

    const getFileNameFromUrl = (url) => {
        if (!url) return "";
        try {
            const decodedUrl = decodeURIComponent(url);
            const parts = decodedUrl.split("/");
            const fileName = parts[parts.length - 1];
            const nameParts = fileName.split("_");
            if (nameParts.length > 1 && nameParts[0].length > 20) {
                return nameParts.slice(1).join("_");
            }
            return fileName;
        } catch (e) {
            return url.split("/").pop();
        }
    };

    const handleFileUpload = async (sectionType, fileToUpload, declarationId) => {
        if (!fileToUpload || !declarationId) {
            showFeedback("error", "Please select a file to upload.");
            return;
        }

        if (!validateUploadFile(fileToUpload, "proof file")) {
            return;
        }

        setLoading(true);
        setUploadingId(declarationId);
        setMessage({ type: "", text: "" });
        const formData = new FormData();
        formData.append("proof", fileToUpload);
        formData.append("id", declarationId);

        try {
            const response = await postData(API_ENDPOINTS.uploadInvestmentProof, formData);
            if (response.statusCode === 200 || response.statusCode === 201) {
                showFeedback("success", "Proof uploaded successfully!");
                fetchTaxData();
            } else {
                showFeedback("error", getApiErrorMessage(response, "Failed to upload proof."));
            }
        } catch (error) {
            showFeedback(
                "error",
                error.response?.data?.message || "Error uploading proof.",
            );
        } finally {
            setLoading(false);
            setUploadingId("");
        }
    };

    const handleForm12BUpload = useCallback(async (file) => {
        if (!file) return;

        if (!validateUploadFile(file, "Form 12B file")) {
            return;
        }
        if (getAbsoluteFileUrl(form12BData)) {
            showFeedback("error", "Form 12B is already uploaded for this financial year.");
            return;
        }

        setLoading(true);
        setUploadingId("form12B");
        const formData = new FormData();
        formData.append("form12A", file); // Backend still expects "form12A" field name
        formData.append("employeePersonalId", userId);
        formData.append("financialYear", financialYear);

        try {
            const response = await postData(API_ENDPOINTS.uploadForm, formData);
            if (response && (response.status === "SUCCESS" || response.success)) {
                showFeedback("success", "Form 12B uploaded successfully!");
                fetchForm12BData();
            } else {
                showFeedback("error", getApiErrorMessage(response, "Failed to upload Form 12B."));
            }
        } catch (error) {
            showFeedback(
                "error",
                error.response?.data?.message || "Error uploading Form 12B.",
            );
        } finally {
            setLoading(false);
            setUploadingId("");
        }
    }, [fetchForm12BData, financialYear, form12BData, getApiErrorMessage, showFeedback, validateUploadFile, userId]);

    const [showTaxDetailsModel, setShowTaxDetailsModel] = useState(false);
    const [taxComparisonData, setTaxComparisonData] = useState(null);
    const [showRegimeChangeDialog, setShowRegimeChangeDialog] = useState(false);
    const [showRegimeRequestDialog, setShowRegimeRequestDialog] = useState(false);
    const [regimeRequestReason, setRegimeRequestReason] = useState("");

    const handleRegimeChangeRequest = async () => {
        if (!regimeRequestReason.trim()) {
            setMessage({ type: "error", text: "Please provide a reason for the regime change request" });
            return;
        }

        try {
            setLoading(true);
            const userId = localStorage.getItem("userId");
            const requestedRegime = taxSummary?.regime === "Old Regime" ? "New Regime" : "Old Regime";

            const response = await postData(API_ENDPOINTS.submitTaxRegimeChangeRequest({
                userId,
                currentRegime: taxSummary?.regime,
                requestedRegime,
                reason: regimeRequestReason,
                financialYear
            }));

            if (response.statusCode === 200 || response.success) {
                setMessage({
                    type: "success",
                    text: `Regime change request sent to HR. You requested to switch from ${taxSummary?.regime} to ${requestedRegime}.`
                });

                setShowRegimeRequestDialog(false);
                setRegimeRequestReason("");

                // Refresh tax data to update the flag
                fetchTaxData();
            } else {
                setMessage({ type: "error", text: response.message || "Failed to send request" });
            }

        } catch (error) {
            setMessage({ type: "error", text: "Failed to send regime change request" });
        } finally {
            setLoading(false);
        }
    };

    const getStatusChip = (status) => {
        switch (status?.toUpperCase()) {
            case "APPROVED":
            case "VERIFIED":
                return <Chip label="Approved" color="success" size="small" icon={<CheckCircleIcon />} />;
            case "PENDING":
                return <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />;
            case "REJECTED":
                return <Chip label="Rejected" color="error" size="small" icon={<CancelIcon />} />;
            case "NOT_SUBMITTED":
                return <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />;
            default:
                return <Chip label="Unknown" size="small" />;
        }
    };

    return (
        <Box>
            {/* Financial Year Selector */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Financial Year</InputLabel>
                                <Select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} label="Financial Year">
                                    <MenuItem value="2023">2023-2024</MenuItem>
                                    <MenuItem value="2024">2024-2025</MenuItem>
                                    <MenuItem value="2025">2025-2026</MenuItem>
                                    <MenuItem value="2026">2026-2027</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Messages */}
            {message.text && (
                <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: "", text: "" })}>
                    {message.text}
                </Alert>
            )}
            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>{message.text}</Alert>
            </Snackbar>

            {taxSummary?.regime === "Old Regime" && (
                <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1565c0" }}>
                                Form 12B Information
                            </Typography>
                            <Tooltip
                                title={
                                    <Box sx={{ p: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">What is Form 12B?</Typography>
                                        <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                            Form 12B is a statement required to be furnished by an employee who joins an organization in the middle of a financial year.
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight="bold">Who needs to submit it?</Typography>
                                        <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                            Any employee who received salary from a previous employer during the current financial year.
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight="bold">When is it required?</Typography>
                                        <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                            It is required to accurately calculate TDS by considering your total income and taxes paid for the current FY. It is not mandatory if you worked only in this organization during this financial year.
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight="bold">What details are included?</Typography>
                                        <Typography variant="caption" display="block">
                                            Previous employer salary, TDS deducted, Professional Tax, Employee PF, and other deductions.
                                        </Typography>
                                    </Box>
                                }
                                arrow
                                placement="right"
                            >
                                <IconButton size="small">
                                    <InfoIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3} alignItems="center">
                            <Grid size={{ xs: 12, sm: 12, md: 8 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Please upload your signed Form 12B if you have joined in the middle of the financial year and have income from a previous employer.
                                    <Typography variant="caption" display="block" color="info.main" sx={{ mt: 1 }}>
                                        Note: This is not mandatory unless you worked in another organization during this financial year.
                                    </Typography>
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                                {isForm12BLoading ? (
                                    <CircularProgress size={24} />
                                ) : getAbsoluteFileUrl(form12BData) ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => window.open(getAbsoluteFileUrl(form12BData), "_blank")}
                                            sx={{ height: '56px', backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
                                        >
                                            Show Preview
                                        </Button>
                                        <Typography variant="caption" color="success.main" align="center" sx={{ mt: 1 }}>
                                            Uploaded on: {new Date((Array.isArray(form12BData) ? form12BData[0] : form12BData).createdAt).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            fullWidth
                                            startIcon={uploadingId === "form12B" ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                            disabled={loading || isFinancialYearEnded}
                                            sx={{ height: '56px' }}
                                        >
                                            {uploadingId === "form12B" ? "Uploading..." : "Upload Form 12B"}
                                            <input type="file" hidden accept=".pdf,image/*" onChange={(e) => handleForm12BUpload(e.target.files[0])} />
                                        </Button>
                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1, fontWeight: 'medium' }}>
                                            (PDF Only, Max 20MB)
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Employee Identification */}
            <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                            <Typography variant="body2" color="text.secondary">Employee ID</Typography>
                            <Typography variant="h6">{taxSummary?.employeeCode || "N/A"}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                            <Typography variant="body2" color="text.secondary">Employee Name</Typography>
                            <Typography variant="h6">{taxSummary?.employeeName || "N/A"}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                            <Typography variant="body2" color="text.secondary">Department</Typography>
                            <Typography variant="h6">{taxSummary?.departmentName || "N/A"}</Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Tax Regime Calculation Comparison */}
            <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Compare both regimes based on your gross salary and declarations.
                    </Typography>
                    <Box sx={{ mb: 3, maxWidth: 400 }}>
                        <TextField
                            fullWidth
                            label="Annual Gross Salary"
                            type="number"
                            value={taxSummary?.grossAnnual || ""}
                            sx={{ bgcolor: 'white' }}
                            InputProps={{
                                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                            }}
                            onChange={(e) => {
                                const newGrossSalary = parseFloat(e.target.value) || 0;
                                setTaxSummary(prev => ({ ...prev, grossAnnual: newGrossSalary }));

                                if (newGrossSalary > 0) {
                                    if (window.taxCalculationTimeout) {
                                        clearTimeout(window.taxCalculationTimeout);
                                    }

                                    window.taxCalculationTimeout = setTimeout(() => {
                                        handleCalculateTax(newGrossSalary);
                                    }, 1000);
                                }
                            }}
                        />
                    </Box>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    bgcolor: taxSummary?.regime === "Old Regime" ? "#e3f2fd" : "white",
                                    cursor: 'pointer',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        boxShadow: 3,
                                        transform: 'translateY(-2px)'
                                    },
                                    border: taxSummary?.regime === "Old Regime" ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.12)'
                                }}
                                onClick={async () => {
                                    if (loading) return;
                                    await handleCalculateTax();
                                }}
                            >
                                <Typography variant="h6" color="primary">Old Tax Regime</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" sx={{ mb: 1 }}>Estimated Annual Tax</Typography>
                                <Typography variant="h4">₹{taxSummary?.oldRegimeTax?.toLocaleString() || 0}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    With all exemptions (80C, HRA, etc.)
                                </Typography>
                                {taxSummary?.regime === "Old Regime" && (
                                    <Chip label="Current" color="primary" size="small" sx={{ mt: 1 }} />
                                )}
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    bgcolor: taxSummary?.regime === "New Regime" ? "#e3f2fd" : "white",
                                    cursor: 'pointer',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        boxShadow: 3,
                                        transform: 'translateY(-2px)'
                                    },
                                    border: taxSummary?.regime === "New Regime" ? '2px solid #9c27b0' : '1px solid rgba(0,0,0,0.12)'
                                }}
                                onClick={async () => {
                                    if (loading) return;
                                    await handleCalculateTax();
                                }}
                            >
                                <Typography variant="h6" color="secondary">New Tax Regime</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" sx={{ mb: 1 }}>Estimated Annual Tax</Typography>
                                <Typography variant="h4">₹{taxSummary?.newRegimeTax?.toLocaleString() || 0}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Lower tax rates, no exemptions
                                </Typography>
                                {taxSummary?.regime === "New Regime" && (
                                    <Chip label="Current" color="secondary" size="small" sx={{ mt: 1 }} />
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {taxComparisonData && (
                            <Button
                                variant="outlined"
                                onClick={() => setShowTaxDetailsModel(true)}
                            >
                                View Detailed Breakdown
                            </Button>
                        )}

                        {!isSubmitted && taxSummary?.regimeUpdateCount < 1 && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setShowRegimeChangeDialog(true);
                                }}
                                disabled={regimeLoading || isFinancialYearEnded}
                            >
                                Switch Regime
                            </Button>
                        )}

                        {!isSubmitted && taxSummary?.regimeUpdateCount >= 1 && !taxSummary?.regimeChangeRequestSent && (
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => {
                                    setShowRegimeRequestDialog(true);
                                }}
                            >
                                Request Regime Change
                            </Button>
                        )}

                        {!isSubmitted && taxSummary?.regimeUpdateCount >= 1 && taxSummary?.regimeChangeRequestSent && (
                            <Chip
                                label="Request Sent to HR"
                                color="info"
                                variant="outlined"
                                icon={<PendingIcon />}
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Regime Change Confirmation Dialog */}
            <Dialog open={showRegimeChangeDialog} onClose={() => setShowRegimeChangeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Tax Regime Change</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Your current regime: {taxSummary?.regime || "Not Selected"}
                        </Typography>
                        <Typography variant="body2">
                            You want to switch to: {taxSummary?.regime === "Old Regime" ? "New Regime" : "Old Regime"}
                        </Typography>
                    </Alert>
                    <Typography variant="body2" paragraph>
                        <strong>Important:</strong> You can change your tax regime only <strong>ONE TIME</strong> per financial year.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        After this change, if you want to switch again, you must send a request to HR. HR will review and may or may not approve your request.
                    </Typography>
                    <Typography variant="body2">
                        Are you sure you want to proceed with this change?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRegimeChangeDialog(false)}>Cancel</Button>
                    <Button
                        onClick={async () => {
                            setShowRegimeChangeDialog(false);
                            await handleRegimeChange();
                        }}
                        variant="contained"
                        color="primary"
                    >
                        Confirm Change
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Regime Change Request Dialog (for employees who already changed once) */}
            <Dialog open={showRegimeRequestDialog} onClose={() => setShowRegimeRequestDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Request Regime Change from HR</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            You have already used your one-time regime change for this financial year.
                        </Typography>
                        <Typography variant="body2">
                            To change your regime again, you need HR approval. Please provide a reason for your request.
                        </Typography>
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Current Regime: {taxSummary?.regime || "Not Selected"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Requested Regime: {taxSummary?.regime === "Old Regime" ? "New Regime" : "Old Regime"}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Reason for Request"
                        value={regimeRequestReason}
                        onChange={(e) => setRegimeRequestReason(e.target.value)}
                        placeholder="Please explain why you need to change your tax regime again..."
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setShowRegimeRequestDialog(false);
                        setRegimeRequestReason("");
                    }}>Cancel</Button>
                    <Button
                        onClick={handleRegimeChangeRequest}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={20} /> : "Send Request to HR"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detailed Breakdown Dialog */}
            <Dialog open={showTaxDetailsModel} onClose={() => setShowTaxDetailsModel(false)} maxWidth="md" fullWidth>
                <DialogTitle>Detailed Tax Breakdown</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                            <Typography variant="h6" color="primary" gutterBottom>Old Regime</Typography>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Gross Annual Salary</TableCell>
                                        <TableCell align="right">₹{taxSummary?.grossAnnual?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Standard Deduction</TableCell>
                                        <TableCell align="right">- ₹{taxComparisonData?.oldRegime?.standardDeduction?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Exemptions (80C, HRA, etc.)</TableCell>
                                        <TableCell align="right">- ₹{(taxComparisonData?.oldRegime?.totalExemptions - taxComparisonData?.oldRegime?.standardDeduction)?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    {taxComparisonData?.oldRegime?.deductionsBreakdown && Object.entries(taxComparisonData.oldRegime.deductionsBreakdown).map(([key, value]) => (
                                        value > 0 && (
                                            <TableRow key={key}>
                                                <TableCell sx={{ pl: 4, color: 'text.secondary', fontSize: '0.8rem' }}>
                                                    {key === 'section80C' ? '80C' :
                                                        key === 'section80D' ? '80D' :
                                                            key === 'professionalTax' ? 'Professional Tax' :
                                                                key === 'homeLoan' ? 'Home Loan Interest' :
                                                                    key === 'other' ? 'Other Sources' :
                                                                        key.toUpperCase()}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                    ₹{value.toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    ))}
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Taxable Income</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{taxComparisonData?.oldRegime?.taxableIncome?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Base Tax</TableCell>
                                        <TableCell align="right">₹{taxComparisonData?.oldRegime?.baseTax?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Cess (4%)</TableCell>
                                        <TableCell align="right">₹{taxComparisonData?.oldRegime?.cess?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Total Tax</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'primary.main' }}>₹{taxComparisonData?.oldRegime?.taxPayable?.toLocaleString()}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                            <Typography variant="h6" color="secondary" gutterBottom>New Regime</Typography>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Gross Annual Salary</TableCell>
                                        <TableCell align="right">₹{taxSummary?.grossAnnual?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Standard Deduction</TableCell>
                                        <TableCell align="right">- ₹{taxComparisonData?.newRegime?.standardDeduction?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Exemptions</TableCell>
                                        <TableCell align="right">- ₹0 (Not Applicable)</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Taxable Income</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{taxComparisonData?.newRegime?.taxableIncome?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Base Tax</TableCell>
                                        <TableCell align="right">₹{taxComparisonData?.newRegime?.baseTax?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Cess (4%)</TableCell>
                                        <TableCell align="right">₹{taxComparisonData?.newRegime?.cess?.toLocaleString()}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Total Tax</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'secondary.main' }}>₹{taxComparisonData?.newRegime?.taxPayable?.toLocaleString()}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowTaxDetailsModel(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Government Rules URLs */}
            <Card sx={{ mb: 3, border: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight="bold">Quick Links:</Typography>
                        <Button
                            size="small"
                            component="a"
                            href="https://www.incometax.gov.in"
                            target="_blank"
                            startIcon={<VisibilityIcon fontSize="small" />}
                        >
                            Income Tax Portal
                        </Button>
                        <Button
                            size="small"
                            component="a"
                            href="https://www.incometax.gov.in/iec/foportal/help/individual/return-applicable-1"
                            target="_blank"
                            startIcon={<VisibilityIcon fontSize="small" />}
                        >
                            Old vs New Tax Regime Details
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Declaration Form */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            Submit IT Declarations
                            <Tooltip title="Declare your tax-saving investments here">
                                <InfoIcon fontSize="small" color="action" />
                            </Tooltip>
                        </Typography>
                        {taxSummary?.regime !== "New Regime" && (
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', border: '1px dashed #1976d2', p: 1, borderRadius: 1 }}>
                                Instructions: Only PDF files allowed (Max size 20MB)
                            </Typography>
                        )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {isSubmitted && taxSummary?.regime !== "New Regime" && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Your IT declarations for Financial Year {financialYear}-{parseInt(financialYear) + 1} have been submitted.
                            Declarations are now locked and cannot be modified. You can still upload proofs for your declared investments below.
                        </Alert>
                    )}

                    {!isSubmissionOpen && isSubmitted && taxSummary?.regime !== "New Regime" && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            The submission window for Financial Year {financialYear}-{parseInt(financialYear) + 1} has ended ({deadlineFormatted}). You can no longer upload proofs or modify declarations for this period.
                        </Alert>
                    )}

                    {isMarchWarning && isSubmissionOpen && taxSummary?.regime !== "New Regime" && (
                        <Alert severity="warning" sx={{ mb: 2, fontWeight: 'bold' }}>
                            ⚠️ DEADLINE APPROACHING: The last date for tax proof submission is {deadlineFormatted}. Please ensure all your proofs are uploaded before the financial year ends.
                        </Alert>
                    )}

                    {!isSubmitted && taxSummary?.regime !== "New Regime" && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Please submit your IT declarations first before uploading proofs.
                        </Alert>
                    )}

                    {isFinancialYearEnded && taxSummary?.regime !== "New Regime" && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Proof uploads for financial year {financialYear}-{parseInt(financialYear) + 1} are disabled.
                            The window ended on {deadlineFormatted}.
                        </Alert>
                    )}

                    {taxSummary?.regime === "New Regime" ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            You have opted for the New Tax Regime. Investment declarations (80C, 80D, HRA, etc.) are not applicable and tax is calculated at lower rates without exemptions.
                        </Alert>
                    ) : (
                        <>
                            {/* Section 80C */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6">Section 80C Investments</Typography>
                                    {!isSubmitted && (
                                        <Button
                                            startIcon={<AddIcon />}
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleAddDeclaration("section80C")}
                                        >
                                            Add More
                                        </Button>
                                    )}
                                </Box>
                                {validationErrors.section80C && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.section80C}</Alert>
                                )}
                                <Grid container spacing={2}>
                                    {declarations.section80C.map((row, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Investment Type</InputLabel>
                                                    <Select
                                                        value={row.investmentType}
                                                        onChange={(e) => handleDeclarationChange("section80C", index, "investmentType", e.target.value)}
                                                        label="Investment Type"
                                                        disabled={isSubmitted}
                                                    >
                                                        {taxOptions["80C"]?.map((option, i) => (
                                                            <MenuItem key={i} value={option}>{option}</MenuItem>
                                                        )) || <MenuItem value="PPF">PPF</MenuItem>}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Amount"
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) => handleDeclarationChange("section80C", index, "amount", e.target.value)}
                                                    disabled={isSubmitted}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                {!row.proofUrl ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            fullWidth
                                                            startIcon={uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading || !row.amount || parseFloat(row.amount) <= 0}
                                                            sx={{ height: '56px' }}
                                                        >
                                                            {uploadingId === row.id ? "Uploading..." : "Upload Proof"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept=".pdf,image/*"
                                                                onChange={(e) => handleFileUpload("80C", e.target.files[0], row.id)}
                                                            />
                                                        </Button>
                                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                                            (PDF, Max 20MB)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '56px',
                                                        px: 2,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 1,
                                                        backgroundColor: '#f9f9f9'
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            maxWidth: '120px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {getFileNameFromUrl(row.proofUrl)}
                                                            {row.proofSize && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                                                                    ({row.proofSize})
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View Proof">
                                                                <IconButton size="small" onClick={() => window.open(getAbsoluteFileUrl(row.proofUrl), "_blank")}>
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!(row.proofStatus?.toUpperCase() === "APPROVED" ||
                                                                row.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                                row.proofStatus?.toUpperCase() === "REJECTED") && (
                                                                    <Tooltip title="Re-upload Proof">
                                                                        <IconButton
                                                                            size="small"
                                                                            component="label"
                                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading}
                                                                        >
                                                                            {uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept=".pdf,image/*"
                                                                                onChange={(e) => handleFileUpload("80C", e.target.files[0], row.id)}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                                {!isSubmitted && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveDeclaration("section80C", index)}
                                                        disabled={declarations.section80C.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Max combined limit for Section 80C: ₹{SECTION_LIMITS["80C"].toLocaleString()}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Section 80D */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6">Section 80D (Health Insurance, etc.)</Typography>
                                    {!isSubmitted && (
                                        <Button
                                            startIcon={<AddIcon />}
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleAddDeclaration("section80D")}
                                        >
                                            Add More
                                        </Button>
                                    )}
                                </Box>
                                {validationErrors.section80D && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.section80D}</Alert>
                                )}
                                <Grid container spacing={2}>
                                    {declarations.section80D.map((row, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Investment Type</InputLabel>
                                                    <Select
                                                        value={row.investmentType}
                                                        onChange={(e) => handleDeclarationChange("section80D", index, "investmentType", e.target.value)}
                                                        label="Investment Type"
                                                        disabled={isSubmitted}
                                                    >
                                                        {taxOptions["80D"]?.map((option, i) => (
                                                            <MenuItem key={i} value={option}>{option}</MenuItem>
                                                        )) || <MenuItem value="Health Insurance">Health Insurance</MenuItem>}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Amount"
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) => handleDeclarationChange("section80D", index, "amount", e.target.value)}
                                                    disabled={isSubmitted}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                {!row.proofUrl ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            fullWidth
                                                            startIcon={uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading || !row.amount || parseFloat(row.amount) <= 0}
                                                            sx={{ height: '56px' }}
                                                        >
                                                            {uploadingId === row.id ? "Uploading..." : "Upload Proof"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept=".pdf,image/*"
                                                                onChange={(e) => handleFileUpload("80D", e.target.files[0], row.id)}
                                                            />
                                                        </Button>
                                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                                            (PDF, Max 20MB)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '56px',
                                                        px: 2,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 1,
                                                        backgroundColor: '#f9f9f9'
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            maxWidth: '120px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {getFileNameFromUrl(row.proofUrl)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View Proof">
                                                                <IconButton size="small" onClick={() => window.open(getAbsoluteFileUrl(row.proofUrl), "_blank")}>
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!(row.proofStatus?.toUpperCase() === "APPROVED" ||
                                                                row.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                                row.proofStatus?.toUpperCase() === "REJECTED") && (
                                                                    <Tooltip title="Re-upload Proof">
                                                                        <IconButton
                                                                            size="small"
                                                                            component="label"
                                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading}
                                                                        >
                                                                            {uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept=".pdf,image/*"
                                                                                onChange={(e) => handleFileUpload("80D", e.target.files[0], row.id)}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                                {!isSubmitted && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveDeclaration("section80D", index)}
                                                        disabled={declarations.section80D.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Max combined limit for Section 80D: ₹{SECTION_LIMITS["80D"].toLocaleString()}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* HRA */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Typography variant="h6">HRA Declarations</Typography>
                                    {!isSubmitted && (
                                        <Button
                                            startIcon={<AddIcon />}
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleAddDeclaration("hra")}
                                        >
                                            Add More
                                        </Button>
                                    )}
                                </Box>
                                <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 2, fontStyle: 'italic', fontWeight: 'medium' }}>
                                    * Upload clear PDF of rent receipt, agreement, landlord PAN, and payment proof for HRA verification.
                                </Typography>
                                {validationErrors.hra && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.hra}</Alert>
                                )}
                                <Grid container spacing={2}>
                                    {declarations.hra.map((row, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>HRA - Type</InputLabel>
                                                    <Select
                                                        value={row.investmentType}
                                                        onChange={(e) => handleDeclarationChange("hra", index, "investmentType", e.target.value)}
                                                        label="HRA - Type"
                                                        disabled={isSubmitted}
                                                    >
                                                        {taxOptions["HRA"]?.map((option, i) => (
                                                            <MenuItem key={i} value={option}>{option}</MenuItem>
                                                        )) || <MenuItem value="HRA">HRA</MenuItem>}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Amount"
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) => handleDeclarationChange("hra", index, "amount", e.target.value)}
                                                    disabled={isSubmitted}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                {!row.proofUrl ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            fullWidth
                                                            startIcon={uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading || !row.amount || parseFloat(row.amount) <= 0}
                                                            sx={{ height: '56px' }}
                                                        >
                                                            {uploadingId === row.id ? "Uploading..." : "Upload Proof"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept=".pdf,image/*"
                                                                onChange={(e) => handleFileUpload("HRA", e.target.files[0], row.id)}
                                                            />
                                                        </Button>
                                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                                            (PDF, Max 20MB)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '56px',
                                                        px: 2,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 1,
                                                        backgroundColor: '#f9f9f9'
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            maxWidth: '120px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {getFileNameFromUrl(row.proofUrl)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View Proof">
                                                                <IconButton size="small" onClick={() => window.open(getAbsoluteFileUrl(row.proofUrl), "_blank")}>
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!(row.proofStatus?.toUpperCase() === "APPROVED" ||
                                                                row.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                                row.proofStatus?.toUpperCase() === "REJECTED") && (
                                                                    <Tooltip title="Re-upload Proof">
                                                                        <IconButton
                                                                            size="small"
                                                                            component="label"
                                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading}
                                                                        >
                                                                            {uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept=".pdf,image/*"
                                                                                onChange={(e) => handleFileUpload("HRA", e.target.files[0], row.id)}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                                {!isSubmitted && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveDeclaration("hra", index)}
                                                        disabled={declarations.hra.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Home Loan Interest */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6">Home Loan Interest</Typography>
                                    {!isSubmitted && (
                                        <Button
                                            startIcon={<AddIcon />}
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleAddDeclaration("homeLoanInterest")}
                                        >
                                            Add More
                                        </Button>
                                    )}
                                </Box>
                                {validationErrors.homeLoanInterest && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.homeLoanInterest}</Alert>
                                )}
                                <Grid container spacing={2}>
                                    {declarations.homeLoanInterest.map((row, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Home Loan - Type</InputLabel>
                                                    <Select
                                                        value={row.investmentType}
                                                        onChange={(e) => handleDeclarationChange("homeLoanInterest", index, "investmentType", e.target.value)}
                                                        label="Home Loan - Type"
                                                        disabled={isSubmitted}
                                                    >
                                                        {taxOptions["homeLoanInterest"]?.map((option, i) => (
                                                            <MenuItem key={i} value={option}>{option}</MenuItem>
                                                        )) || <MenuItem value="Home Loan Interest">Home Loan Interest</MenuItem>}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Amount"
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) => handleDeclarationChange("homeLoanInterest", index, "amount", e.target.value)}
                                                    disabled={isSubmitted}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                {!row.proofUrl ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            fullWidth
                                                            startIcon={uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading || !row.amount || parseFloat(row.amount) <= 0}
                                                            sx={{ height: '56px' }}
                                                        >
                                                            {uploadingId === row.id ? "Uploading..." : "Upload Proof"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept=".pdf,image/*"
                                                                onChange={(e) => handleFileUpload("homeLoanInterest", e.target.files[0], row.id)}
                                                            />
                                                        </Button>
                                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                                            (PDF, Max 20MB)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '56px',
                                                        px: 2,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 1,
                                                        backgroundColor: '#f9f9f9'
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            maxWidth: '120px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {getFileNameFromUrl(row.proofUrl)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View Proof">
                                                                <IconButton size="small" onClick={() => window.open(getAbsoluteFileUrl(row.proofUrl), "_blank")}>
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!(row.proofStatus?.toUpperCase() === "APPROVED" ||
                                                                row.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                                row.proofStatus?.toUpperCase() === "REJECTED") && (
                                                                    <Tooltip title="Re-upload Proof">
                                                                        <IconButton
                                                                            size="small"
                                                                            component="label"
                                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading}
                                                                        >
                                                                            {uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept=".pdf,image/*"
                                                                                onChange={(e) => handleFileUpload("homeLoanInterest", e.target.files[0], row.id)}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                                {!isSubmitted && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveDeclaration("homeLoanInterest", index)}
                                                        disabled={declarations.homeLoanInterest.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Max combined limit for Home Loan: ₹{SECTION_LIMITS["homeLoanInterest"].toLocaleString()}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* NPS */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6">Section 80CCD (NPS)</Typography>
                                    {!isSubmitted && (
                                        <Button
                                            startIcon={<AddIcon />}
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleAddDeclaration("nps")}
                                        >
                                            Add More
                                        </Button>
                                    )}
                                </Box>
                                {validationErrors.nps && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.nps}</Alert>
                                )}
                                <Grid container spacing={2}>
                                    {declarations.nps.map((row, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>NPS - Type</InputLabel>
                                                    <Select
                                                        value={row.investmentType}
                                                        onChange={(e) => handleDeclarationChange("nps", index, "investmentType", e.target.value)}
                                                        label="NPS - Type"
                                                        disabled={isSubmitted}
                                                    >
                                                        {taxOptions["NPS"]?.map((option, i) => (
                                                            <MenuItem key={i} value={option}>{option}</MenuItem>
                                                        )) || <MenuItem value="NPS">NPS</MenuItem>}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Amount"
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) => handleDeclarationChange("nps", index, "amount", e.target.value)}
                                                    disabled={isSubmitted}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                {!row.proofUrl ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            fullWidth
                                                            startIcon={uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading || !row.amount || parseFloat(row.amount) <= 0}
                                                            sx={{ height: '56px' }}
                                                        >
                                                            {uploadingId === row.id ? "Uploading..." : "Upload Proof"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept=".pdf,image/*"
                                                                onChange={(e) => handleFileUpload("NPS", e.target.files[0], row.id)}
                                                            />
                                                        </Button>
                                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                                            (PDF, Max 20MB)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '56px',
                                                        px: 2,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 1,
                                                        backgroundColor: '#f9f9f9'
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            maxWidth: '120px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {getFileNameFromUrl(row.proofUrl)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View Proof">
                                                                <IconButton size="small" onClick={() => window.open(getAbsoluteFileUrl(row.proofUrl), "_blank")}>
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!(row.proofStatus?.toUpperCase() === "APPROVED" ||
                                                                row.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                                row.proofStatus?.toUpperCase() === "REJECTED") && (
                                                                    <Tooltip title="Re-upload Proof">
                                                                        <IconButton
                                                                            size="small"
                                                                            component="label"
                                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading}
                                                                        >
                                                                            {uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept=".pdf,image/*"
                                                                                onChange={(e) => handleFileUpload("NPS", e.target.files[0], row.id)}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                                {!isSubmitted && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveDeclaration("nps", index)}
                                                        disabled={declarations.nps.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Max combined limit for NPS: ₹{SECTION_LIMITS["NPS"]?.toLocaleString() || '50,000'}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6">Other Income</Typography>
                                    {!isSubmitted && (
                                        <Button
                                            startIcon={<AddIcon />}
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleAddDeclaration("otherIncome")}
                                        >
                                            Add More
                                        </Button>
                                    )}
                                </Box>
                                {validationErrors.otherIncome && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.otherIncome}</Alert>
                                )}
                                <Grid container spacing={2}>
                                    {declarations.otherIncome.map((row, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Income Type</InputLabel>
                                                    <Select
                                                        value={row.investmentType}
                                                        onChange={(e) => handleDeclarationChange("otherIncome", index, "investmentType", e.target.value)}
                                                        label="Income Type"
                                                        disabled={isSubmitted}
                                                    >
                                                        {taxOptions["otherDeductions"]?.map((option, i) => (
                                                            <MenuItem key={i} value={option}>{option}</MenuItem>
                                                        )) || <MenuItem value="Other">Other</MenuItem>}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Amount"
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) => handleDeclarationChange("otherIncome", index, "amount", e.target.value)}
                                                    disabled={isSubmitted}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                                {!row.proofUrl ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            fullWidth
                                                            startIcon={uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading || !row.amount || parseFloat(row.amount) <= 0}
                                                            sx={{ height: '56px' }}
                                                        >
                                                            {uploadingId === row.id ? "Uploading..." : "Upload Proof"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept=".pdf,image/*"
                                                                onChange={(e) => handleFileUpload("otherIncome", e.target.files[0], row.id)}
                                                            />
                                                        </Button>
                                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                                            (PDF, Max 20MB)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '56px',
                                                        px: 2,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 1,
                                                        backgroundColor: '#f9f9f9'
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            maxWidth: '120px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {getFileNameFromUrl(row.proofUrl)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View Proof">
                                                                <IconButton size="small" onClick={() => window.open(getAbsoluteFileUrl(row.proofUrl), "_blank")}>
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!(row.proofStatus?.toUpperCase() === "APPROVED" ||
                                                                row.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                                row.proofStatus?.toUpperCase() === "REJECTED") && (
                                                                    <Tooltip title="Re-upload Proof">
                                                                        <IconButton
                                                                            size="small"
                                                                            component="label"
                                                                            disabled={!isProofPhase || !isSubmitted || isFinancialYearEnded || loading}
                                                                        >
                                                                            {uploadingId === row.id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept=".pdf,image/*"
                                                                                onChange={(e) => handleFileUpload("otherIncome", e.target.files[0], row.id)}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                                {!isSubmitted && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveDeclaration("otherIncome", index)}
                                                        disabled={declarations.otherIncome.length <= 1}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            </Box>
                            {!isSubmitted && (
                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmitDeclarations}
                                        disabled={loading}
                                        size="large"
                                    >
                                        {loading ? <CircularProgress size={24} /> : "Submit Declarations"}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Pending Proofs Table */}
            {taxSummary?.regime !== "New Regime" && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Submitted Proofs
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {proofsLoading ?
                            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                <CircularProgress />
                            </Box>
                            : pendingProofs.length > 0 ?
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Section Type</TableCell>
                                                <TableCell>Amount Declared</TableCell>
                                                <TableCell>Submitted On</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Comments</TableCell>
                                                {pendingProofs.some(p => p.proofUrl &&
                                                    !(p.proofStatus?.toUpperCase() === "APPROVED" ||
                                                        p.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                        p.proofStatus?.toUpperCase() === "REJECTED")) && (
                                                        <TableCell>Actions</TableCell>
                                                    )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pendingProofs.map((proof, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{proof.section || "N/A"}</TableCell>
                                                    <TableCell>₹{(proof.declaredAmount || proof.amount || 0).toLocaleString()}</TableCell>
                                                    <TableCell>
                                                        {proof.createdAt ? new Date(proof.createdAt).toLocaleDateString() : "N/A"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getStatusChip(proof.proofStatus)}
                                                        {proof.proofSize && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                Size: {proof.proofSize}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{proof.proofRemarks || proof.comments || "-"}</TableCell>
                                                    {pendingProofs.some(p => p.proofUrl &&
                                                        !(p.proofStatus?.toUpperCase() === "APPROVED" ||
                                                            p.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                            p.proofStatus?.toUpperCase() === "REJECTED")) && (
                                                            <TableCell>
                                                                {proof.proofUrl &&
                                                                    !(proof.proofStatus?.toUpperCase() === "APPROVED" ||
                                                                        proof.proofStatus?.toUpperCase() === "VERIFIED" ||
                                                                        proof.proofStatus?.toUpperCase() === "REJECTED") && (
                                                                        <Tooltip title="View Document">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => window.open(getAbsoluteFileUrl(proof.proofUrl), "_blank")}
                                                                            >
                                                                                <VisibilityIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                            </TableCell>
                                                        )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                :
                                <Alert severity="info">No proofs submitted yet. Upload your investment proofs above.</Alert>
                        }
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default EmployeeITDeclarations;